import React, { useEffect, useCallback, useMemo, useReducer, useRef } from 'react';
import { StyleSheet, View, ActivityIndicator, Animated, Platform } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { APP_NAME, API_HOST } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import SubmitButton from '@/components/Buttons/SubmitButton';
import AuthPage from '@/components/Pages/AuthPage';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import Link from '@/components/Typography/Link';
import { useAuthStore } from '@/store';
import { trackRetentionEvent, RETENTION_ACTION } from '@/constants/utils/analyticsEvents';

const REWARD_COLOR = '#10B981';
const CONFETTI = [
  { x: -120, y: -60, color: '#F59E0B', rotate: '20deg' },
  { x: 110, y: -80, color: '#3B82F6', rotate: '-30deg' },
  { x: -80, y: 70, color: '#EC4899', rotate: '45deg' },
  { x: 130, y: 50, color: '#8B5CF6', rotate: '10deg' },
  { x: 10, y: -125, color: '#10B981', rotate: '60deg' },
  { x: -140, y: 0, color: '#06B6D4', rotate: '-15deg' },
  { x: 70, y: 110, color: '#F97316', rotate: '75deg' },
];

const createStyles = (colors) => StyleSheet.create({
  rewardAmount: {
    fontSize: 44,
    letterSpacing: -1.5,
    color: REWARD_COLOR,
    textAlign: 'center',
  },
  confettiDot: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: 2,
  },
  contentContainer: {
    width: '100%',
    gap: 12,
    alignItems: 'center',
  },
  title: {
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 320,
    marginBottom: 12,
  },
  submitButton: {
    width: 240,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  successIconContainer: {
    backgroundColor: REWARD_COLOR,
    ...(Platform.OS === 'web' ? { boxShadow: '0 14px 34px rgba(16,185,129,0.35)' } : {}),
  },
  errorIconContainer: {
    backgroundColor: '#FEE2E2',
  },
  loadingIconContainer: {
    backgroundColor: colors.solid_color_secondary,
  },
  footer: {
    gap: 12,
    marginTop: 24,
    alignItems: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
  },
});

// State reducer
const verifyReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false, success: false };
    case 'SET_SUCCESS':
      return { ...state, success: true, error: '', isLoading: false, creditsGranted: action.payload || 0 };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  isLoading: false,
  error: '',
  success: false,
  creditsGranted: 0,
};

// Memoized footer links component
const FormLinks = ({ t, styles }) => (
  <View style={styles.footer}>
    <View style={styles.footerLinks}>
      <Link href="/login">
        {t('auth.verifyEmail.buttons.goToLogin')}
      </Link>
    </View>
  </View>
);


// Memoized status icon component
const StatusIcon = React.memo(({ styles, colors, status }) => {
  if (status === 'loading') {
    return (
      <View style={[styles.iconContainer, styles.loadingIconContainer]}>
        <ActivityIndicator size="large" color={colors.text_color_primary} />
      </View>
    );
  }

  if (status === 'error') {
    return (
      <View style={[styles.iconContainer, styles.errorIconContainer]}>
        <Icon name="close" size={40} color="#EF4444" />
      </View>
    );
  }

  if (status === 'success') {
    return (
      <View style={[styles.iconContainer, styles.successIconContainer]}>
        <Icon name="check" size={40} color="#fff" solid />
      </View>
    );
  }

  return null;
});

// Success hero: check mark springs in while a handful of confetti squares
// scatter outward. Pure Animated, so it works on web and native alike.
const SuccessHero = ({ styles, colors, creditsGranted }) => {
  const pop = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.spring(pop, { toValue: 1, friction: 5, tension: 80, useNativeDriver: Platform.OS !== 'web' }).start();
  }, [pop]);

  return (
    <View style={{ alignItems: 'center' }}>
      <View style={{ position: 'relative', alignItems: 'center', justifyContent: 'center' }}>
        {CONFETTI.map((c, i) => (
          <Animated.View
            key={i}
            style={[styles.confettiDot, {
              backgroundColor: c.color,
              opacity: pop.interpolate({ inputRange: [0, 0.6, 1], outputRange: [0, 1, 0.85] }),
              transform: [
                { translateX: pop.interpolate({ inputRange: [0, 1], outputRange: [0, c.x] }) },
                { translateY: pop.interpolate({ inputRange: [0, 1], outputRange: [0, c.y] }) },
                { rotate: c.rotate },
                { scale: pop.interpolate({ inputRange: [0, 1], outputRange: [0.2, 1] }) },
              ],
            }]}
          />
        ))}
        <Animated.View style={{ transform: [{ scale: pop.interpolate({ inputRange: [0, 1], outputRange: [0.4, 1] }) }] }}>
          <StatusIcon styles={styles} colors={colors} status="success" />
        </Animated.View>
      </View>
      {creditsGranted > 0 && (
        <Text weight="bold" style={styles.rewardAmount}>+{creditsGranted}</Text>
      )}
    </View>
  );
};

SuccessHero.propTypes = {
  styles: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  creditsGranted: PropTypes.number,
};

StatusIcon.propTypes = {
  styles: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  status: PropTypes.oneOf(['loading', 'error', 'success']).isRequired,
};

function VerifyEmailRoute() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const params = useLocalSearchParams();
  const token = params?.token;

  // Initialize state with reducer
  const [state, dispatch] = useReducer(verifyReducer, {
    ...initialState,
    isLoading: !!token,
    error: token ? '' : t('auth.verifyEmail.errors.noToken'),
  });

  // Memoize styles
  const styles = useMemo(() => createStyles(colors), [colors]);

  // If the user opened the link in the same browser they signed up in, they're
  // still logged in: send them straight back to the app (with a fresh balance)
  // instead of bouncing them to the login form.
  const isLoggedIn = useAuthStore(state => !!state.user?.id && !!state.token);
  const isEmailVerified = useAuthStore(state => !!state.user?.flags?.isEmailVerified);

  // Completing the inbox link is the last signup step. The refreshed account
  // confirms verification before a signed-in user goes straight into Chat.
  useEffect(() => {
    if (state.success && isLoggedIn && isEmailVerified) router.replace('/(app)/app');
  }, [state.success, isLoggedIn, isEmailVerified]);

  const handleContinue = useCallback(() => {
    router.replace(isLoggedIn ? '/app' : '/');
  }, [isLoggedIn]);

  const handleLogoPress = useCallback(() => {
    router.push('/');
  }, []);

  // Effect for email verification
  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        dispatch({ type: 'SET_ERROR', payload: t('auth.verifyEmail.errors.noToken') });
        return;
      }

      try {
        const response = await fetch(`${API_HOST}/json/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          throw new Error(t('auth.verifyEmail.errors.invalidToken'));
        }

        const data = await response.json().catch(() => ({}));
        dispatch({ type: 'SET_SUCCESS', payload: Number(data?.creditsGranted) || 0 });
        trackRetentionEvent(RETENTION_ACTION.EMAIL_VERIFIED, 'email_link');

        // Same-browser case: pull the verified flag + new balance into the store
        // so the app reflects the credits the instant they navigate back.
        const auth = useAuthStore.getState();
        if (auth.user?.id && auth.token) {
          auth.fetchUserSlim?.().catch?.(() => {});
          auth.fetchUserCredits?.({ force: true }).catch?.(() => {});
        }
      } catch (err) {
        dispatch({ 
          type: 'SET_ERROR', 
          payload: err.message || t('auth.verifyEmail.errors.verificationFailed')
        });
      }
    };

    if (token) {
      verifyEmail();
    }
  }, [token]);

  // Memoize content rendering
  const renderContent = useMemo(() => {
    if (state.error) {
      return (
        <>
          <StatusIcon styles={styles} colors={colors} status="error" />
          <Text size='xxl' weight='bold' color='primary'>{t('auth.verifyEmail.errorTitle')}</Text>
          <Text style={styles.subtitle}>{state.error}</Text>
        </>
      );
    }

    if (state.isLoading) {
      return (
        <>
          <StatusIcon styles={styles} colors={colors} status="loading" />
          <Text style={styles.title}>{t('auth.verifyEmail.loadingTitle')}</Text>
          <Text style={styles.subtitle}>{t('auth.verifyEmail.loadingSubtitle')}</Text>
        </>
      );
    }


    if (state.success) {
      const hasReward = state.creditsGranted > 0;
      const credits = String(state.creditsGranted);
      let subtitleKey = 'auth.verifyEmail.successSubtitle';
      if (isLoggedIn) subtitleKey = hasReward ? 'auth.verifyEmail.successSubtitleRewardLoggedIn' : 'auth.verifyEmail.successSubtitleLoggedIn';
      else if (hasReward) subtitleKey = 'auth.verifyEmail.successSubtitleReward';
      return (
        <>
          <SuccessHero styles={styles} colors={colors} creditsGranted={state.creditsGranted} />
          <Text size='xxl' weight='bold' color='primary' style={styles.title}>
            {hasReward ? t('auth.verifyEmail.successTitleReward', { credits }) : t('auth.verifyEmail.successTitle')}
          </Text>
          <Text color='secondary' style={styles.subtitle}>{t(subtitleKey, { credits })}</Text>
          <SubmitButton
            onPress={handleContinue}
            title={isLoggedIn ? t('auth.verifyEmail.buttons.backToApp') : t('auth.verifyEmail.buttons.goToLogin')}
            style={styles.submitButton}
          />
        </>
      );
    }
  }, [state.isLoading, state.error, state.success, state.creditsGranted, isLoggedIn, styles, colors, t, handleContinue]);

  return (
    <AuthPage
      title={t('auth.verifyEmail.title')}
      onLogoPress={handleLogoPress}
      showHeader={!state.isLoading}
    >
      <MetaHead 
        title="Verify Email"
        description="Complete your Gab AI account setup by verifying your email address. This helps secure your account and ensures you receive important updates about our AI services."
        noindex
      />
      <View style={styles.contentContainer}>
        {renderContent}
      </View>

      {!state.success && <FormLinks t={t} styles={styles} />}
    </AuthPage>
  );
};

export default React.memo(VerifyEmailRoute);
